import { createAction, createSlice } from "@reduxjs/toolkit";
import {
  BulkDoctorInvitationRequest,
  BulkRowError,
  DoctorInvitationDto,
  DoctorInvitationRequest,
} from "../../types/doctorInvitation.definition";

export type InvitationApiError = {
  code: string;
  message?: string;
  data?: BulkRowError[];
};

// State
interface DoctorInvitationState {
  invitations: DoctorInvitationDto[];
  loading: boolean;
  error: InvitationApiError | null;
  inviteLoading: boolean;
  resendLoading: boolean;
  cancelLoading: boolean;
  bulkInviteLoading: boolean;
}

const initialState: DoctorInvitationState = {
  invitations: [],
  loading: false,
  error: null,
  inviteLoading: false,
  resendLoading: false,
  cancelLoading: false,
  bulkInviteLoading: false,
};

// Action creators for saga
export const fetchInvitationsRequest = createAction(
  "doctorInvitations/fetchInvitationsRequest"
);
export const fetchInvitationsSuccess = createAction<DoctorInvitationDto[]>(
  "doctorInvitations/fetchInvitationsSuccess"
);
export const fetchInvitationsFailure = createAction<InvitationApiError>(
  "doctorInvitations/fetchInvitationsFailure"
);

export const inviteDoctorRequest = createAction<DoctorInvitationRequest>(
  "doctorInvitations/inviteDoctorRequest"
);
export const inviteDoctorSuccess = createAction<DoctorInvitationDto>(
  "doctorInvitations/inviteDoctorSuccess"
);
export const inviteDoctorFailure = createAction<InvitationApiError>(
  "doctorInvitations/inviteDoctorFailure"
);

export const resendInvitationRequest = createAction<number>(
  "doctorInvitations/resendInvitationRequest"
);
export const resendInvitationSuccess = createAction<DoctorInvitationDto>(
  "doctorInvitations/resendInvitationSuccess"
);
export const resendInvitationFailure = createAction<InvitationApiError>(
  "doctorInvitations/resendInvitationFailure"
);

export const cancelInvitationRequest = createAction<number>(
  "doctorInvitations/cancelInvitationRequest"
);
export const cancelInvitationSuccess = createAction<number>(
  "doctorInvitations/cancelInvitationSuccess"
);
export const cancelInvitationFailure = createAction<InvitationApiError>(
  "doctorInvitations/cancelInvitationFailure"
);

export const bulkInviteDoctorsRequest =
  createAction<BulkDoctorInvitationRequest>(
    "doctorInvitations/bulkInviteDoctorsRequest"
  );
export const bulkInviteDoctorsSuccess = createAction<DoctorInvitationDto[]>(
  "doctorInvitations/bulkInviteDoctorsSuccess"
);
export const bulkInviteDoctorsFailure = createAction<InvitationApiError>(
  "doctorInvitations/bulkInviteDoctorsFailure"
);

// Slice
const doctorInvitationSlice = createSlice({
  name: "doctorInvitations",
  initialState,
  reducers: {
    clearInvitationError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    // Fetch invitations
    builder
      .addCase(fetchInvitationsRequest, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvitationsSuccess, (state, action) => {
        state.loading = false;
        state.invitations = action.payload;
        state.error = null;
      })
      .addCase(fetchInvitationsFailure, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Invite doctor
    builder
      .addCase(inviteDoctorRequest, state => {
        state.inviteLoading = true;
        state.error = null;
      })
      .addCase(inviteDoctorSuccess, (state, action) => {
        state.inviteLoading = false;
        state.invitations.unshift(action.payload);
        state.error = null;
      })
      .addCase(inviteDoctorFailure, (state, action) => {
        state.inviteLoading = false;
        state.error = action.payload;
      });

    // Resend invitation
    builder
      .addCase(resendInvitationRequest, state => {
        state.resendLoading = true;
        state.error = null;
      })
      .addCase(resendInvitationSuccess, (state, action) => {
        state.resendLoading = false;
        const index = state.invitations.findIndex(
          inv => inv.id === action.payload.id
        );
        if (index !== -1) {
          state.invitations[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(resendInvitationFailure, (state, action) => {
        state.resendLoading = false;
        state.error = action.payload;
      });

    // Cancel invitation
    builder
      .addCase(cancelInvitationRequest, state => {
        state.cancelLoading = true;
        state.error = null;
      })
      .addCase(cancelInvitationSuccess, (state, action) => {
        state.cancelLoading = false;
        state.invitations = state.invitations.filter(
          inv => inv.id !== action.payload
        );
        state.error = null;
      })
      .addCase(cancelInvitationFailure, (state, action) => {
        state.cancelLoading = false;
        state.error = action.payload;
      });

    // Bulk invite doctors
    builder
      .addCase(bulkInviteDoctorsRequest, state => {
        state.bulkInviteLoading = true;
        state.error = null;
      })
      .addCase(bulkInviteDoctorsSuccess, (state, action) => {
        state.bulkInviteLoading = false;
        state.invitations.unshift(...action.payload);
        state.error = null;
      })
      .addCase(bulkInviteDoctorsFailure, (state, action) => {
        state.bulkInviteLoading = false;
        state.error = action.payload;
      });
  },
});

// Export slice actions
export const { clearInvitationError } = doctorInvitationSlice.actions;

// Export selectors
export const selectInvitations = (state: {
  doctorInvitations: DoctorInvitationState;
}) => state.doctorInvitations.invitations;

export const selectInvitationsLoading = (state: {
  doctorInvitations: DoctorInvitationState;
}) => state.doctorInvitations.loading;

export const selectInvitationsError = (state: {
  doctorInvitations: DoctorInvitationState;
}) => state.doctorInvitations.error;

export const selectInviteLoading = (state: {
  doctorInvitations: DoctorInvitationState;
}) => state.doctorInvitations.inviteLoading;

export const selectResendLoading = (state: {
  doctorInvitations: DoctorInvitationState;
}) => state.doctorInvitations.resendLoading;

export const selectCancelLoading = (state: {
  doctorInvitations: DoctorInvitationState;
}) => state.doctorInvitations.cancelLoading;

export const selectBulkInviteLoading = (state: {
  doctorInvitations: DoctorInvitationState;
}) => state.doctorInvitations.bulkInviteLoading;

export default doctorInvitationSlice.reducer;
